import type { Code } from "mdast";

export interface ParsedFenceInfo {
  kind: string;
  attrs: Record<string, string>;
}

/**
 * Classify a fence body as a *reference embed*: a bare `@ref` and nothing else
 * (comment-only lines ignored). Returns the ref without the leading `@`, or
 * null for an inline query. This is a lightweight UI/routing check — a bare
 * `@ref` already runs today (the server expands the include), so this only
 * decides how the embed is edited. Kept in step with dwsql's lexer
 * (isRefStart/isRefChar in shared/dwsql/lex.ts).
 */
export function refOf(body: string): string | null {
  const stripped = body
    .split("\n")
    .filter((l) => l.trim() !== "" && !l.trimStart().startsWith("--"))
    .join("\n")
    .trim();
  const m = /^@([A-Za-z_./][A-Za-z0-9_./-]*)$/.exec(stripped);
  return m ? m[1] : null;
}

/** Directory portion of a repo path ("a/b/c.md" → "a/b", "c.md" → ""). */
function dirOf(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? "" : p.slice(0, i);
}

/**
 * Client mirror of the server's `@ref` resolver (makeResolver in
 * server/compileQuery.ts): from the page's directory, try `<dir>/<ref>.sql`
 * then `<dir>/queries/<ref>.sql`, walking up to the repo root; first existing
 * file wins. Returns the resolved repo-relative path, or null. `exists` probes
 * the repo's file set (the client already has it from the tree). Keep the two
 * search orders identical so the UI links to the same file the server runs.
 */
export function resolveRefPath(
  ref: string,
  pagePath: string | null,
  exists: (path: string) => boolean,
): string | null {
  const join = (a: string, b: string): string => (a ? `${a}/${b}` : b);
  let dir = dirOf(pagePath ?? "");
  for (;;) {
    for (const cand of [`${ref}.sql`, `queries/${ref}.sql`]) {
      const rel = join(dir, cand);
      if (exists(rel)) return rel;
    }
    if (dir === "") break;
    dir = dirOf(dir);
  }
  return null;
}

/** Parse a fence info string like: metric label="Recent users" */
export function parseFenceInfo(info: string): ParsedFenceInfo {
  const trimmed = info.trim();
  const firstSpace = trimmed.search(/\s/);
  const kind = (firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace))
    .trim()
    .toLowerCase();
  const rest = firstSpace === -1 ? "" : trimmed.slice(firstSpace + 1);
  const attrs: Record<string, string> = {};
  const re = /([a-zA-Z_][\w-]*)\s*=\s*("([^"]*)"|'([^']*)'|(\S+))/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rest))) {
    attrs[m[1]] = m[3] ?? m[4] ?? m[5] ?? "";
  }
  return { kind, attrs };
}

/** Quote an attribute value so parseFenceInfo round-trips it. */
function quoteValue(v: string): string {
  if (v !== "" && !/[\s"']/.test(v)) return v;
  // parseFenceInfo's quoted forms are "([^"]*)" / '([^']*)' — no escapes. Pick
  // whichever quote the value doesn't contain; if it has both, drop the
  // single quotes (rare, and the alternative is silent corruption).
  if (!v.includes('"')) return `"${v}"`;
  return `'${v.replace(/'/g, "")}'`;
}

/** Inverse of parseFenceInfo: build `kind key=val key="v w"` from parts. */
export function serializeFenceInfo(
  kind: string,
  attrs: Record<string, string>,
): string {
  const parts = [kind];
  for (const [k, v] of Object.entries(attrs)) {
    parts.push(`${k}=${quoteValue(v)}`);
  }
  return parts.join(" ");
}

export interface FenceLocation {
  /** Leading whitespace before the opening fence (spaces/tabs only). */
  indent: string;
  /** The fence delimiter run from the source, e.g. "```" or "~~~~". */
  delim: string;
  /** Byte offsets of the whole code node in the source string. */
  start: number;
  end: number;
  raw: string;
}

/**
 * Locate a code node's fence in the source, or return null if it can't be
 * spliced safely. We bail when the fence isn't preceded purely by whitespace
 * (e.g. a `> ` blockquote prefix) — those edits aren't a local splice. Note
 * mdast puts start.offset at the first backtick, so the indent lives in the
 * text just *before* the node and is preserved by the splice automatically.
 */
export function fenceAt(source: string, node: Code): FenceLocation | null {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  if (start == null || end == null) return null;
  const lineStart = source.lastIndexOf("\n", start - 1) + 1;
  const indent = source.slice(lineStart, start);
  if (!/^[ \t]*$/.test(indent)) return null; // blockquote / non-trivial prefix
  const raw = source.slice(start, end);
  const m = raw.match(/^(`{3,}|~{3,})/);
  if (!m) return null;
  return { indent, delim: m[1], start, end, raw };
}

/** Whether an embed at this node can be edited via spliceFence. */
export function canSplice(source: string, node: Code): boolean {
  return fenceAt(source, node) !== null;
}

function maxRun(str: string, ch: string): number {
  let max = 0;
  let cur = 0;
  for (const c of str) {
    if (c === ch) {
      cur++;
      if (cur > max) max = cur;
    } else {
      cur = 0;
    }
  }
  return max;
}

/**
 * Replace a single fenced code block in `source` and return the new source.
 *
 * Pass `newAttrs` to rewrite the info string (kind is taken from node.lang);
 * omit it to keep the original opening line verbatim and touch only the body.
 *
 * Scope: backtick/tilde code fences whose body is inert, so the splice is
 * purely local. Container directives (`:::`) would make this non-local — an
 * inner run can force *enclosing* fences to grow — so they're out of scope
 * here and `fenceAt` only recognizes ` ``` ` / `~~~`.
 */
export function spliceFence(
  source: string,
  node: Code,
  newBody: string,
  newAttrs?: Record<string, string>,
): string {
  const loc = fenceAt(source, node);
  if (!loc) throw new Error("spliceFence: node is not an editable fence");
  const { indent, delim, start, end, raw } = loc;
  const fenceChar = delim[0];

  // Grow the delimiter run so it always exceeds any run inside the new body;
  // never shrink below the original, to preserve the author's style.
  const need = Math.max(delim.length, maxRun(newBody, fenceChar) + 1);
  const fence = fenceChar.repeat(need);

  let info: string;
  if (newAttrs) {
    info = serializeFenceInfo(node.lang ?? "", newAttrs);
  } else {
    const nl = raw.indexOf("\n");
    info = raw.slice(delim.length, nl === -1 ? raw.length : nl);
  }

  const bodyLines = newBody === "" ? [] : newBody.split("\n");
  const block = [
    fence + info,
    ...bodyLines.map((l) => indent + l),
    indent + fence,
  ].join("\n");

  return source.slice(0, start) + block + source.slice(end);
}
